// Club page titles: name, place and what it is, and nothing else.
//
// Every title used to end in "(2026) | sunbedly". A year in 4,800 titles is a
// pattern a spam classifier reads as freshness-baiting, and it said nothing a
// searcher needed. What a searcher does need is the name, the town and the
// kind of place, inside the ~60 characters Google shows.
//
// Uniqueness comes from name + town, the same pair the slug is made of; two
// clubs with the same name in the same town would also share a slug, which
// the database forbids.

export const TITLE_MAX = 60;
const BRAND = " | sunbedly";

// Words that already say "this is a beach club" in one of the six languages.
// When the name carries one of these, adding the type again reads as
// "Lido Blase, lido in Marzamemi".
const TYPE_IN_NAME = [
  /beach\s?club/i,
  /\bbeach\b/i,
  /\blido\b/i,
  /\bbagn[oi]\b/i,
  /stabilimento/i,
  /chiringuito/i,
  /club de playa/i,
  /strandbad/i,
  /\bplage\b/i,
  /\bstrand\b/i,
];

export type ClubTitleInput = {
  name: string;
  // The town as it should read in the page's language (townName() applied).
  town: string;
  // "beach club" / "lido" / "Beachclub": the bare type word for this language.
  typeWord: string;
  // "beach club in Marzamemi": the type word with the town, in this language.
  typeInTown: string;
};

export function clubTitle({ name, town, typeWord, typeInTown }: ClubTitleInput): string {
  const clean = name.trim();
  const nameHasType =
    TYPE_IN_NAME.some((re) => re.test(clean)) ||
    (typeWord ? clean.toLowerCase().includes(typeWord.toLowerCase()) : false);
  const nameHasTown = town ? clean.toLowerCase().includes(town.toLowerCase()) : true;

  // "En Vogue, beach club in Šibenik" / "Lido Blase, Marzamemi" /
  // "Olbia Sunbeds, beach club" / "Kai Beach Olbia".
  let core: string;
  if (!nameHasType && !nameHasTown) core = `${clean}, ${typeInTown}`;
  else if (nameHasType && !nameHasTown) core = `${clean}, ${town}`;
  else if (!nameHasType && nameHasTown) core = `${clean}, ${typeWord}`;
  else core = clean;

  // Give up the least useful part first: the brand, then the type, then the
  // town. The name itself is only ever cut when it alone exceeds the limit.
  const candidates = [core + BRAND, core, `${clean}, ${town}`, clean];
  for (const c of candidates) if (c.length <= TITLE_MAX) return c;
  return clean.slice(0, TITLE_MAX - 1).trimEnd() + "…";
}
